import React, { FC, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Spinner, Box, Button, Image, Text, Flex } from "@chakra-ui/react";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import StandardModal from "@/js/components/shared/StandardModal";
import iconClock from "@/assets/IconClock.svg?url";
import { endTimeIntToUnixTimestamp } from "@/js/utils/timeUtils";
import { useSelectedClassroomStore } from "@/stores/v2/selectedClassroomStore";
import { colours } from "@/utils/themeUtils";
import { ClassroomSessionTimeSelector } from "../ClassroomSessionTimeSelector";
import { oneMinuteFromNow, padTimes } from "../ClassroomSessionTimeSelectorHelpers";
import { useClassroomSessionStore } from "@/stores/v2/classroomSessionStore";
import { envConfig } from "@/config";
import { fetchLastSessionEndTime } from "@/js/api/sessionsApi";
import { useQuery } from "@tanstack/react-query";
import { getClassroomStore } from "@/stores/classroomStore";
import useStore from "@/stores";
import { Notice } from "@/components/Notice";
import { useAccountSettingsUse24Hour } from "@/query/accountSettingsQuery";

dayjs.extend(customParseFormat);

interface Props {
	open: boolean;
	onStartError: boolean;
	inClassroom: boolean;
	isMonitoringClass?: boolean;
	studentCount: number;
}

const ClassroomStartSession: FC<Props> = (props) => {
	const { t } = useTranslation();
	const { classroomId, classroomStableId } = useSelectedClassroomStore((state) => ({
		classroomId: state.id,
		classroomStableId: state.stableIdV1,
	}));

	const teacherStableId = getClassroomStore(useStore.getState()).getTeacherStableIdV1() as string;

	const use24HourTime = useAccountSettingsUse24Hour();

	const [timeValue, setTimeValue] = useState<number | null>(null);
	const [error, setError] = useState<string | null>(null);
	// This error comes from timer and decides whether save button should be disabled
	const [hasTimeError, setHasTimeError] = useState<string | null>(null);
	const [studentCountError, setStudentCountError] = useState<string | null>(null);
	const [requestOngoing, setRequestOngoing] = useState<boolean | null>(null);

	const fetchLastSessionEndTimeQuery = useQuery({
		queryKey: [teacherStableId, classroomStableId],
		queryFn: async () => {
			// This is not possible. When a claas is selected, teacher and classroom stable IDs are already known. Just for type safety
			// If these aren't available, prefill time selector default to one minute from now
			if (!teacherStableId || !classroomStableId) {
				return;
			}
			return await fetchLastSessionEndTime(teacherStableId, classroomStableId);
		},
	});

	useEffect(() => {
		if (!props.open) {
			setTimeValue(null);
			setError(null);
		}
		if (props.onStartError) {
			setError(t("overview.cannotStartClassDueToRestrictions"));
			setRequestOngoing(false);
		}
	}, [props.open, props.onStartError, t]);

	useEffect(() => {
		const MaxStudents = envConfig.MAX_STUDENTS;
		if (props.studentCount && props.studentCount > MaxStudents) {
			setStudentCountError(t("overview.cannotStartClassWithTooManyStudents", { max: MaxStudents }));
		} else {
			setStudentCountError(null);
		}
	}, [props.studentCount, t]);

	const timeSelectedHandler = (value: number | null, hasTimeError: string | null) => {
		setTimeValue(value);
		setError(null);
		setHasTimeError(hasTimeError);
		setRequestOngoing(false);
	};

	const handleStartMonitoringSession = () => {
		if (timeValue) {
			void useClassroomSessionStore.getState().handleStartClassSession({
				classroomId: classroomId as string,
				expiry: endTimeIntToUnixTimestamp(timeValue),
				force: false,
				inClassroom: props.inClassroom,
			});

			setTimeValue(null);
			setError(null);
			setRequestOngoing(true);
		} else if (!props.onStartError) {
			setError(t("overview.pleaseSelectValidTime"));
		}
	};

	const handleStartRegularSession = () => {
		if (timeValue) {
			void useClassroomSessionStore.getState().handleStartClassSession({
				classroomId: classroomId as string,
				expiry: endTimeIntToUnixTimestamp(timeValue),
				inClassroom: props.inClassroom,
			});

			setTimeValue(null);
			setError(null);
			setRequestOngoing(true);
		} else if (!props.onStartError) {
			setError(t("overview.pleaseSelectValidTime"));
		}
	};

	const handleStartSession = () => {
		if (!timeValue) return;
		// Ensure the end time is in the future
		const timeObject = dayjs(padTimes(timeValue), "HHmm", true);
		if (timeObject.isBefore(oneMinuteFromNow())) {
			setError(t("overview.endTimeCannotBeInPast"));
			return;
		}

		if (props.isMonitoringClass) {
			handleStartMonitoringSession();
		} else {
			handleStartRegularSession();
		}
	};

	const handleClose = () => {
		useClassroomSessionStore.getState().handleShowStartClassModal({ show: false });
		setTimeValue(null);
		setError(null);
		setRequestOngoing(false);
	};

	const renderErrorText = () => {
		if ((error === null || error === "") && (studentCountError === null || studentCountError === "")) {
			return <></>;
		}

		const errorText = studentCountError ?? error;
		return (
			<Text position="absolute" color={colours.danger.background.strong} width="209px" p="4px 0 0 4px">
				{errorText}
			</Text>
		);
	};

	const renderActions = () => (
		<Button
			onClick={handleStartSession}
			isLoading={requestOngoing ?? false}
			isDisabled={error !== null || hasTimeError !== null || studentCountError !== null}
		>
			{t("overview.start")}
		</Button>
	);

	const queryAndRenderSessionEndTime = () => {
		if (fetchLastSessionEndTimeQuery.isLoading) {
			return <Spinner size="sm" />;
		}

		let preselected: string | number = oneMinuteFromNow().format("hh:mm A");
		if (fetchLastSessionEndTimeQuery.data && fetchLastSessionEndTimeQuery.data.lastEndTime > 0) {
			const end = dayjs.unix(fetchLastSessionEndTimeQuery.data.lastEndTime);
			preselected = use24HourTime ? +end.format("HHmm") : end.format("hh:mm A");
		} else if (use24HourTime) {
			preselected = +oneMinuteFromNow().format("HHmm");
		}

		return (
			<Box>
				<ClassroomSessionTimeSelector use24Hour={use24HourTime} onChange={timeSelectedHandler} preselected={preselected} />
				{renderErrorText()}
			</Box>
		);
	};

	const render = () => {
		return (
			<StandardModal
				size="medium"
				title={props.isMonitoringClass ? t("overview.startMonitoringSession") : t("overview.startClassSession")}
				actions={renderActions()}
				open={props.open}
				close={handleClose}
			>
				<form onSubmit={(ev) => ev.preventDefault()}>
					<Notice
						status="info"
						children={
							use24HourTime ? (
								<Trans i18nKey="overview.notice24HourStart" components={{ bold: <b /> }} />
							) : (
								<Trans i18nKey="overview.notice12HourStart" components={{ bold: <b /> }} />
							)
						}
					/>
					<Flex alignItems="top" gap={3} py={1} mt={4}>
						<Flex alignItems="center" gap={2} py={1} my={0} height={"38px"}>
							<Image src={iconClock} alt="" />
							<Text>{t("overview.sessionEndsAt")}</Text>
						</Flex>
						{queryAndRenderSessionEndTime()}
					</Flex>
				</form>
			</StandardModal>
		);
	};

	return render();
};

export default ClassroomStartSession;
